import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { SystemMember } from '@/hooks/useSystemMembers';
import { 
  Shield, 
  Crown, 
  User, 
  Building2, 
  Activity, 
  Calendar, 
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface MemberCardProps {
  member: SystemMember;
}

const getRoleBadgeConfig = (role: SystemMember['platform_role']) => {
  switch (role) {
    case 'super_admin':
      return {
        label: 'Super Admin',
        icon: Crown,
        className: 'bg-gradient-to-r from-purple-600 to-purple-700 text-white border-purple-300',
      };
    case 'admin':
      return {
        label: 'Admin',
        icon: Shield,
        className: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-300',
      };
    case 'member':
      return {
        label: 'Member',
        icon: User,
        className: 'bg-gradient-to-r from-green-600 to-green-700 text-white border-green-300',
      };
    case 'guest':
    default:
      return {
        label: 'Guest',
        icon: User,
        className: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white border-gray-300',
      };
  }
};

const getEngagementConfig = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high':
      return { icon: TrendingUp, className: 'text-green-600', label: 'High' };
    case 'medium':
      return { icon: Minus, className: 'text-yellow-600', label: 'Medium' };
    case 'low':
    default:
      return { icon: TrendingDown, className: 'text-red-600', label: 'Low' };
  }
};

export const MemberCard = ({ member }: MemberCardProps) => {
  const roleConfig = getRoleBadgeConfig(member.platform_role);
  const engagementConfig = getEngagementConfig(member.insights.engagement_level);
  const RoleIcon = roleConfig.icon;
  const EngagementIcon = engagementConfig.icon;

  return (
    <Card className="w-full hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-4 space-y-4">
        {/* Header Section */}
        <div className="flex items-start space-x-3">
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage src={member.avatar_url || ''} />
            <AvatarFallback className="text-sm font-medium">
              {member.name?.split(' ').map(n => n[0]).join('') || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base truncate">
              {member.name || 'Unknown'}
            </h3>
            <p className="text-sm text-muted-foreground truncate">
              {member.email || member.phone_number || 'No contact info'}
            </p>
            <p className="text-xs text-muted-foreground">
              Joined {format(new Date(member.created_at), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Role and Status */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={cn("flex items-center gap-1 text-xs", roleConfig.className)}>
            <RoleIcon className="h-3 w-3" />
            {roleConfig.label}
          </Badge>
          
          <div className="flex items-center gap-1">
            {member.insights.onboarding_complete ? (
              <div title="Onboarding complete">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
            ) : (
              <div title="Incomplete onboarding">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              </div>
            )}
            
            {member.insights.has_admin_roles && (
              <div title="Has admin roles">
                <Shield className="h-4 w-4 text-blue-600" />
              </div>
            )}
            
            {member.insights.days_since_last_activity !== null && 
             member.insights.days_since_last_activity > 90 && (
              <div title="Inactive user">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            )}
          </div>
        </div>

        {/* Organizations */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Organizations</h4>
          {member.organization_memberships.length === 0 ? (
            <span className="text-sm text-muted-foreground italic">No organizations</span>
          ) : (
            <div className="space-y-1">
              {member.organization_memberships.slice(0, 3).map((org, index) => (
                <div key={org.org_id} className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">
                    {org.org_name}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {org.role}
                  </Badge>
                  {member.insights.is_multi_org && index === 0 && (
                    <div title="Multi-organization user">
                      <Building2 className="h-3 w-3 text-orange-600" />
                    </div>
                  )}
                </div>
              ))}
              {member.organization_memberships.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{member.organization_memberships.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Activity and Stats */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <EngagementIcon className={cn("h-4 w-4", engagementConfig.className)} />
              <span className="text-sm font-medium">{engagementConfig.label}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {member.insights.activity_score}/mo activity
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{member.stats.total_bookings}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              bookings total
            </div>
          </div>
        </div>

        {/* Last Activity */}
        <div className="text-xs text-muted-foreground pt-1 border-t">
          <div className="flex items-center justify-between">
            <span>Last active:</span>
            <span>
              {member.stats.last_activity_date 
                ? format(new Date(member.stats.last_activity_date), 'MMM d, yyyy')
                : 'Never'
              }
            </span>
          </div>
          {member.insights.days_since_last_activity !== null && (
            <div className="flex items-center justify-between">
              <span>Days ago:</span>
              <span>{member.insights.days_since_last_activity}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};